import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Build as FixIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import { borderRadius, getModernButton, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import {
  getQuickFixesForIssue,
  getAutoFixConfig,
  hasQuickFix,
  QuickFixConfig,
} from '../quickFixConfig';
import { useMappedParameters } from '../hooks/useMappedParameters';
import {
  buildValidationQuickFixPlan,
  useValidationQuickFixFlow,
} from '../hooks/useValidationQuickFixFlow';
import type { ValidationIssue } from '../types';
import { BatchOperationDialog } from '../../../shared/ui/batch';
import {
  OperationProgressIndicator,
  OperationProgressPanel,
  type OperationProgressModel,
} from '../../../shared/ui/progress';
import {
  buildBulkQuickFixContext,
  buildFixAllHelp,
  buildQuickFixHelp,
  mapQuickFixProgress,
  notifyQuickFixResult,
} from '../lib/validationQuickFixPresentation';

interface QuickFixButtonProps {
  issue: ValidationIssue;
  onFixApplied: (validationIds: number[]) => Promise<void> | void;
  enabled?: boolean;
  familyId?: string | null;
  itemId?: string | null;
  snapshotLabel?: string;
}

interface FixAllButtonProps {
  validationId: number;
  issues: ValidationIssue[];
  onFixApplied: (validationIds: number[]) => Promise<void> | void;
  enabled?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
}

/**
 * Per-issue quick fix button with dialog for manual fixes
 */
export function QuickFixButton({
  issue,
  onFixApplied,
  enabled = true,
  familyId = null,
  itemId = null,
  snapshotLabel,
}: QuickFixButtonProps) {
  const theme = useTheme();
  const buttonStyles = getModernButton(theme);
  const fixes = getQuickFixesForIssue(
    issue.ValidationId,
    issue.Message,
    issue.AdditionalData,
    issue.ElementIds.length
  );
  const { resolveRevitParam } = useMappedParameters();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeFix, setActiveFix] = useState<QuickFixConfig | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [dualInputValues, setDualInputValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [quickFixProgress, setQuickFixProgress] = useState<OperationProgressModel | null>(null);
  const snackbar = useSnackbarStore();

  const { applyQuickFixPlan } = useValidationQuickFixFlow();

  if (!enabled) return null;
  if (issue.ElementIds.length === 0) return null;
  if (fixes.length === 0) return null;

  /** Get the PCF attribute name for this fix (from issue data or fix config). */
  const resolvePcfAttribute = (fix: QuickFixConfig) =>
    issue.AdditionalData?.parameterName || fix.parameterName;

  const handleFixOpen = (fix: QuickFixConfig) => {
    setActiveFix(fix);
    setQuickFixProgress(null);
    setInputValue(issue.AdditionalData?.currentValue || '');
    if (fix.fields) {
      const initial: Record<string, string> = {};
      fix.fields.forEach((f) => (initial[f.parameterName] = ''));
      setDualInputValues(initial);
    }
    setDialogOpen(true);
  };

  const quickFixPlan = activeFix
    ? buildValidationQuickFixPlan({
        issue,
        fix: activeFix,
        resolveRevitParam,
        inputValue,
        dualInputValues,
      })
    : null;

  const canApplyPlan =
    activeFix !== null &&
    quickFixPlan !== null &&
    quickFixPlan.operations.length > 0 &&
    (activeFix?.canAutoFix ||
      activeFix?.type === 'dualInput' ||
      inputValue.trim().length > 0);

  const handleQuickFixApply = async () => {
    if (!activeFix || !quickFixPlan) return;
    setLoading(true);
    setQuickFixProgress(null);
    try {
      const { result } = await applyQuickFixPlan({
        plan: quickFixPlan,
        familyId,
        itemId,
        snapshotLabel:
          snapshotLabel ||
          `${activeFix.label} for validation #${issue.ValidationId}`,
        onProgress: (progress) => {
          setQuickFixProgress(mapQuickFixProgress(activeFix.label, progress));
        },
      });

      notifyQuickFixResult(snackbar, result, quickFixPlan.undoAvailable);

      if (result.affectedValidationIds.length > 0) {
        await onFixApplied(result.affectedValidationIds);
      }
    } catch (error: any) {
      snackbar.showError(error.message);
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setQuickFixProgress(null);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={layoutGap.dense} sx={{ alignSelf: 'flex-start' }}>
        {fixes.map((fix, idx) => {
          const help = buildQuickFixHelp(fix, issue.ElementIds.length);

          return fix.canAutoFix ? (
            <ActionHelpTooltip key={idx} help={help} disabled={loading}>
              <Button
                size="small"
                color="primary"
                data-testid="validation-quick-fix"
                startIcon={loading ? <OperationProgressIndicator size={16} ariaLabel="Loading quick fix" /> : <AutoFixIcon />}
                onClick={() => handleFixOpen(fix)}
                disabled={loading}
                sx={{
                  ...buttonStyles.contained,
                  py: layoutGap.dense,
                }}
              >
                {fix.label}
              </Button>
            </ActionHelpTooltip>
          ) : (
            <ActionHelpTooltip key={idx} help={help} disabled={loading}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                data-testid="validation-quick-fix"
                startIcon={<FixIcon />}
                onClick={() => handleFixOpen(fix)}
                disabled={loading}
                sx={{
                  ...buttonStyles.outlined,
                  py: layoutGap.dense,
                }}
              >
                {fix.label}
              </Button>
            </ActionHelpTooltip>
          );
        })}
      </Stack>

      {/* Manual fix dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !loading && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{activeFix?.label}</DialogTitle>
        <DialogContent>
          <Stack spacing={layoutGap.block} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {activeFix?.description}
            </Typography>

            <Alert severity="info" sx={{ borderRadius: `${borderRadius.md}px` }}>
              Quick fix review is required before anything is applied.
            </Alert>

            {quickFixPlan && (
              <Stack spacing={layoutGap.bump}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Planned change
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Parameter: <strong>{resolvePcfAttribute(activeFix || fixes[0])}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current value: <strong>{quickFixPlan.currentValue || 'empty'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Proposed value: <strong>{quickFixPlan.proposedValue || 'Pending input'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Affected elements: <strong>{quickFixPlan.affectedCount}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Targets: {quickFixPlan.affectedElements.slice(0, 3).join(', ') || 'No targets'}
                  {quickFixPlan.affectedElements.length > 3
                    ? ` +${quickFixPlan.affectedElements.length - 3} more`
                    : ''}
                </Typography>
                <Alert
                  severity={quickFixPlan.undoAvailable ? 'success' : 'warning'}
                  sx={{ borderRadius: `${borderRadius.sm}px` }}
                >
                  {quickFixPlan.undoAvailable
                    ? 'Undo will be available after apply.'
                    : 'Undo is limited for this quick fix because the current runtime does not expose the full previous value set.'}
                </Alert>
              </Stack>
            )}

            {loading ? (
              <OperationProgressPanel
                compact
                model={
                  quickFixProgress ?? {
                    title: activeFix?.label || 'Validation quick fix',
                    message: 'Quick fix queued for Revit.',
                    status: 'running',
                    processed: 0,
                    total: quickFixPlan?.affectedCount,
                    percent: null,
                    locked: true,
                  }
                }
              />
            ) : null}

            {issue.AdditionalData?.currentValue && !quickFixPlan?.currentValue && (
              <Alert severity="info" sx={{ borderRadius: `${borderRadius.md}px` }}>
                Current value: <strong>{issue.AdditionalData.currentValue}</strong>
              </Alert>
            )}

            {activeFix?.type === 'dropdown' && activeFix.options && (
              <FormControl fullWidth size="small">
                <InputLabel>Select value</InputLabel>
                <Select
                  value={inputValue}
                  label="Select value"
                  onChange={(e) => setInputValue(e.target.value)}
                >
                  {activeFix.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {activeFix?.type === 'input' && (
              <TextField
                fullWidth
                size="small"
                label={activeFix.label}
                placeholder={activeFix.placeholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            )}

            {activeFix?.type === 'dualInput' &&
              activeFix.fields?.map((field) => (
                <TextField
                  key={field.parameterName}
                  fullWidth
                  size="small"
                  label={field.label}
                  placeholder={field.placeholder}
                  value={dualInputValues[field.parameterName] || ''}
                  onChange={(e) =>
                    setDualInputValues((prev) => ({
                      ...prev,
                      [field.parameterName]: e.target.value,
                    }))
                  }
                />
              ))}

            <Typography variant="caption" color="text.secondary">
              Applying to {issue.ElementIds.length} element{issue.ElementIds.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={loading}
            variant="outlined"
            sx={{
              ...buttonStyles.outlined,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleQuickFixApply}
            disabled={loading || !canApplyPlan}
            startIcon={loading ? <OperationProgressIndicator size={16} ariaLabel="Applying quick fix" /> : <FixIcon />}
            sx={{
              ...buttonStyles.contained,
            }}
          >
            Confirm quick fix
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}

/**
 * "Fix All" button for auto-fixable checks, placed in accordion summary
 */
export function FixAllButton({
  validationId,
  issues,
  onFixApplied,
  enabled = true,
  variant = 'contained',
}: FixAllButtonProps) {
  const theme = useTheme();
  const buttonStyles = getModernButton(theme);
  const autoFix = getAutoFixConfig(validationId);
  const { resolveRevitParam } = useMappedParameters();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickFixProgress, setQuickFixProgress] = useState<OperationProgressModel | null>(null);
  const snackbar = useSnackbarStore();

  const { applyQuickFixPlan } = useValidationQuickFixFlow();

  if (!enabled) return null;
  if (!autoFix || !hasQuickFix(validationId)) return null;

  const { matchingIssues, allElementIds, bulkIssue } = buildBulkQuickFixContext(
    issues,
    autoFix
  );

  if (!bulkIssue || allElementIds.length === 0) return null;
  const bulkPlan = buildValidationQuickFixPlan({
    issue: bulkIssue,
    fix: autoFix,
    resolveRevitParam,
  });
  const fixAllHelp = buildFixAllHelp(autoFix, matchingIssues.length, allElementIds.length);

  const handleFixAll = async () => {
    setLoading(true);
    setQuickFixProgress(null);
    try {
      const { result } = await applyQuickFixPlan({
        plan: bulkPlan,
        snapshotLabel: `Fix All for validation #${validationId}`,
        onProgress: (progress) => {
          setQuickFixProgress(mapQuickFixProgress(autoFix.label, progress));
        },
      });

      notifyQuickFixResult(snackbar, result);

      if (result.affectedValidationIds.length > 0) {
        await onFixApplied(result.affectedValidationIds);
      }
    } catch (error: any) {
      snackbar.showError(error.message);
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setQuickFixProgress(null);
    }
  };

  return (
    <>
      <ActionHelpTooltip help={fixAllHelp} disabled={loading}>
        <Button
          variant={variant}
          color="primary"
          size="small"
          startIcon={loading ? <OperationProgressIndicator size={16} ariaLabel="Applying automatic fixes" /> : <AutoFixIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setQuickFixProgress(null);
            setDialogOpen(true);
          }}
          disabled={loading}
          sx={{
            ...(variant === 'contained'
              ? buttonStyles.contained
              : variant === 'outlined'
                ? buttonStyles.outlined
                : {}),
            py: layoutGap.dense,
          }}
        >
          Fix all ({matchingIssues.length})
        </Button>
      </ActionHelpTooltip>

      <BatchOperationDialog
        open={dialogOpen}
        title={autoFix.label}
        phase={loading ? 'running' : 'confirming'}
        progressModel={quickFixProgress}
        description={
          <Stack spacing={layoutGap.block} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: `${borderRadius.md}px` }}>
              This bulk quick fix will update {bulkPlan.affectedCount} elements across {matchingIssues.length} issues.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              {autoFix.description}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Proposed change: <strong>{bulkPlan.proposedValue}</strong>
            </Typography>
            <Alert
              severity={bulkPlan.undoAvailable ? 'success' : 'warning'}
              sx={{ borderRadius: `${borderRadius.sm}px` }}
            >
              {bulkPlan.undoAvailable
                ? 'Undo will be available after apply.'
                : 'Undo is limited for this bulk quick fix.'}
            </Alert>
          </Stack>
        }
        summary={{
          total: bulkPlan.affectedCount,
          succeeded: 0,
          failed: 0,
        }}
        onClose={() => {
          setQuickFixProgress(null);
          setDialogOpen(false);
        }}
        onConfirm={handleFixAll}
        confirmLabel="Confirm quick fix"
        showItemDetails={false}
      />
    </>
  );
}
